import React, { useState } from "react";
import { useSelector } from "react-redux";

import { makeStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import LinearProgress from "@material-ui/core/LinearProgress";
import Paper from "@material-ui/core/Paper";
import TextField from "@material-ui/core/TextField";

import { db } from "../../../../firebase/firebasejs";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: "20px",
    marginTop: "20px",
    width: "90%",
    backgroundColor: "#f5f5f5",
  },

  button: {
    marginTop: "20px",
    marginBottom: "20px",
  },

  progress: {
    marginTop: "20px",
    marginBottom: "10px",
  },

  status: {
    marginTop: "10px",
  },

  dateField: {
    marginRight: "20px",
    marginTop: "20px",
    minWidth: "220px",
  },
}));

export default function DownloadEventsDeposits() {
  const classes = useStyles();

  const eventsList = useSelector(
    (state) => state.events.events
  );

  /*
   * Fecha local YYYY-MM-DD
   */
  const getLocalDate = () => {
    const date = new Date();

    const year = date.getFullYear();

    const month = String(
      date.getMonth() + 1
    ).padStart(2, "0");

    const day = String(
      date.getDate()
    ).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  /*
   * Rango inicial por defecto
   */
  const [startDate, setStartDate] =
    useState("2021-01-01");

  const [endDate, setEndDate] =
    useState(getLocalDate());

  const [loading, setLoading] =
    useState(false);

  const [processed, setProcessed] =
    useState(0);

  const [totalEvents, setTotalEvents] =
    useState(0);

  const [
    eventsWithDeposits,
    setEventsWithDeposits,
  ] = useState(0);

  const [
    totalMovements,
    setTotalMovements,
  ] = useState(0);

  /*
   * Limpieza fuerte de texto.
   *
   * La idea es garantizar que NINGÚN campo
   * pueda introducir un salto de fila
   * dentro del CSV.
   */
  const cleanCsvText = (value) => {
    if (
      value === null ||
      value === undefined
    ) {
      return "";
    }

    return String(value)
      /*
       * Saltos de línea Windows / Mac / Unix
       */
      .replace(/\r\n/g, " ")
      .replace(/\n/g, " ")
      .replace(/\r/g, " ")

      /*
       * Separadores Unicode de línea
       */
      .replace(/[\u2028\u2029]/g, " ")

      /*
       * Espacios Unicode especiales
       */
      .replace(
        /[\u00A0\u1680\u180E\u2000-\u200A\u202F\u205F\u3000]/g,
        " "
      )

      /*
       * Caracteres invisibles
       */
      .replace(
        /[\u200B-\u200D\u2060\uFEFF]/g,
        ""
      )

      /*
       * Tabs
       */
      .replace(/\t/g, " ")

      /*
       * Vertical tab y form feed
       */
      .replace(/[\v\f]/g, " ")

      /*
       * Guiones raros
       */
      .replace(
        /[\u2010\u2011\u2012\u2013\u2014\u2015\u2212]/g,
        "-"
      )

      /*
       * Comillas simples Unicode
       */
      .replace(
        /[\u2018\u2019\u201A\u201B]/g,
        "'"
      )

      /*
       * Comillas dobles Unicode
       */
      .replace(
        /[\u201C\u201D\u201E\u201F]/g,
        '"'
      )

      /*
       * Puntos suspensivos
       */
      .replace(/\u2026/g, "...")

      /*
       * Caracteres de control ASCII
       * excepto los normales imprimibles
       */
      .replace(
        /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g,
        ""
      )

      /*
       * Espacios consecutivos
       */
      .replace(/ {2,}/g, " ")

      .trim();
  };

  /*
   * Todos los campos van entre comillas.
   *
   * Si existe " dentro del texto:
   *
   * hola "mundo"
   *
   * queda:
   *
   * "hola ""mundo"""
   */
  const escapeCsvField = (value) => {
    const cleanValue =
      cleanCsvText(value);

    return `"${cleanValue.replace(
      /"/g,
      '""'
    )}"`;
  };

  const createCsv = (rows) => {
    if (!rows.length) {
      alert(
        "No se encontraron depósitos para el rango seleccionado."
      );

      return;
    }

    /*
     * Columnas exactas del reporte
     */
    const columns = [
      "iDEvento",
      "eventoFirebase",
      "nombreEvento",
      "fechaEvento",
      "ciudadEvento",
      "paisEvento",
      "tipoDeEvento",

      "depositKey",
      "amount",
      "dateRegistry",
      "depositDate",
      "description",
      "document",
      "index",
      "type",
      "userIdAuth",
    ];

    /*
     * Nombres más cómodos para Excel
     */
    const columnLabels = {
      iDEvento: "ID Evento",
      eventoFirebase: "Evento Firebase",
      nombreEvento: "Evento",
      fechaEvento: "Fecha Evento",
      ciudadEvento: "Ciudad",
      paisEvento: "País",
      tipoDeEvento: "Tipo Evento",

      depositKey: "ID Movimiento",
      amount: "Monto",
      dateRegistry: "Fecha Registro",
      depositDate: "Fecha Depósito",
      description: "Descripción",
      document: "Documento",
      index: "Index",
      type: "Tipo Movimiento",
      userIdAuth: "Usuario",
    };

    const csvRows = [];

    /*
     * Encabezado
     */
    csvRows.push(
      columns
        .map((column) =>
          escapeCsvField(
            columnLabels[column]
          )
        )
        .join(",")
    );

    /*
     * Filas
     */
    rows.forEach((row) => {
      const csvRow = columns
        .map((column) => {
          const value =
            row[column] !== undefined
              ? row[column]
              : "";

          return escapeCsvField(value);
        })
        .join(",");

      csvRows.push(csvRow);
    });

    /*
     * IMPORTANTE PARA EXCEL
     *
     * 1. BOM UTF-8
     * 2. sep=;
     * 3. Separador ;
     * 4. CRLF \r\n
     *
     * Cada celda ya fue limpiada para
     * eliminar saltos internos.
     */
    const csvContent =
      "\uFEFF" +
      "sep=;\r\n" +
      csvRows.join("\r\n");

    const blob = new Blob(
      [csvContent],
      {
        type:
          "text/csv;charset=utf-8;",
      }
    );

    const url =
      URL.createObjectURL(blob);

    const link =
      document.createElement("a");

    link.href = url;

    link.setAttribute(
      "download",
      `deposits_${startDate}_${endDate}.csv`
    );

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);

    /*
     * Le damos tiempo al navegador
     * antes de liberar el objeto.
     */
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 1000);
  };

  const downloadDeposits = async () => {
    if (!eventsList) {
      alert(
        "Todavía no está disponible eventsList."
      );

      return;
    }

    if (!startDate || !endDate) {
      alert(
        "Debes seleccionar fecha de inicio y fecha final."
      );

      return;
    }

    if (startDate > endDate) {
      alert(
        "La fecha de inicio no puede ser mayor que la fecha final."
      );

      return;
    }

    setLoading(true);

    setProcessed(0);

    setTotalEvents(0);

    setEventsWithDeposits(0);

    setTotalMovements(0);

    try {
      /*
       * Tenemos fechaEvento como:
       *
       * 2025-07-13
       *
       * Por lo tanto podemos comparar
       * directamente los strings.
       */
      const eventsArray =
        Object.values(
          eventsList
        ).filter(Boolean);

      /*
       * Eventos dentro del rango
       */
      const filteredEvents =
        eventsArray.filter(
          (event) => {
            if (
              !event.fechaEvento ||
              !event.iDEvento
            ) {
              return false;
            }

            return (
              event.fechaEvento >=
              startDate &&
              event.fechaEvento <=
              endDate
            );
          }
        );

      /*
       * Ordenar por fecha del evento
       */
      filteredEvents.sort(
        (a, b) =>
          String(
            a.fechaEvento
          ).localeCompare(
            String(
              b.fechaEvento
            )
          )
      );

      console.log(
        "========================="
      );

      console.log(
        "RANGO DEL REPORTE:",
        startDate,
        "hasta",
        endDate
      );

      console.log(
        "Eventos totales:",
        eventsArray.length
      );

      console.log(
        "Eventos dentro del rango:",
        filteredEvents.length
      );

      setTotalEvents(
        filteredEvents.length
      );

      if (
        filteredEvents.length === 0
      ) {
        alert(
          "No hay eventos dentro del rango seleccionado."
        );

        return;
      }

      const allRows = [];

      let foundEvents = 0;

      let movementsFound = 0;

      /*
       * Secuencial para no hacer cientos
       * de llamadas simultáneas a Firebase.
       */
      for (
        let i = 0;
        i <
        filteredEvents.length;
        i++
      ) {
        const event =
          filteredEvents[i];

        const eventFirebaseId =
          `evento_${event.iDEvento}`;

        console.log(
          `Procesando ${i + 1}/${filteredEvents.length
          }`,
          eventFirebaseId,
          event.nombreEvento
        );

        try {
          /*
           * Solamente descargamos:
           *
           * deposits/depositsList
           */
          const snapshot =
            await db
              .ref(
                `eventosCompletos/${eventFirebaseId}/deposits/depositsList`
              )
              .once("value");

          if (
            snapshot.exists()
          ) {
            const depositsList =
              snapshot.val();

            let eventHasDeposits =
              false;

            Object.entries(
              depositsList
            ).forEach(
              ([
                depositKey,
                deposit,
              ]) => {
                if (
                  !deposit ||
                  typeof deposit !==
                  "object"
                ) {
                  return;
                }

                eventHasDeposits =
                  true;

                movementsFound++;

                allRows.push({
                  /*
                   * DATOS DEL EVENTO
                   */
                  iDEvento:
                    event.iDEvento,

                  eventoFirebase:
                    eventFirebaseId,

                  nombreEvento:
                    event.nombreEvento ||
                    "",

                  fechaEvento:
                    event.fechaEvento ||
                    "",

                  ciudadEvento:
                    event.ciudadEvento ||
                    "",

                  paisEvento:
                    event.paisEvento ||
                    "",

                  tipoDeEvento:
                    event.tipoDeEvento ||
                    "",

                  /*
                   * DATOS DEL MOVIMIENTO
                   */
                  depositKey,

                  amount:
                    Number(
                      deposit.amount
                    ) || 0,

                  dateRegistry:
                    deposit.dateRegistry ||
                    "",

                  depositDate:
                    deposit.depositDate ||
                    "",

                  description:
                    deposit.description ||
                    "",

                  document:
                    deposit.document ||
                    "",

                  index:
                    deposit.index ||
                    depositKey,

                  type:
                    deposit.type ||
                    "",

                  userIdAuth:
                    deposit.userIdAuth ||
                    "",
                });
              }
            );

            if (
              eventHasDeposits
            ) {
              foundEvents++;

              setEventsWithDeposits(
                foundEvents
              );
            }

            setTotalMovements(
              movementsFound
            );
          }
        } catch (error) {
          console.error(
            `Error leyendo ${eventFirebaseId}:`,
            error
          );
        }

        setProcessed(i + 1);
      }

      /*
       * Ordenar primero por fecha del
       * movimiento.
       *
       * YYYY-MM-DD permite ordenar strings.
       */
      allRows.sort((a, b) => {
        const dateA =
          a.depositDate || "";

        const dateB =
          b.depositDate || "";

        const depositComparison =
          String(
            dateA
          ).localeCompare(
            String(dateB)
          );

        if (
          depositComparison !== 0
        ) {
          return depositComparison;
        }

        /*
         * Si tienen la misma fecha,
         * ordenamos por fecha de registro.
         */
        return String(
          a.dateRegistry || ""
        ).localeCompare(
          String(
            b.dateRegistry || ""
          )
        );
      });

      console.log(
        "========================="
      );

      console.log(
        "PROCESO TERMINADO"
      );

      console.log(
        "Eventos revisados:",
        filteredEvents.length
      );

      console.log(
        "Eventos con depósitos:",
        foundEvents
      );

      console.log(
        "Movimientos encontrados:",
        allRows.length
      );

      console.log(
        "========================="
      );

      createCsv(allRows);
    } catch (error) {
      console.error(
        "Error general:",
        error
      );

      alert(
        "Ocurrió un error procesando los eventos."
      );
    } finally {
      setLoading(false);
    }
  };

  const percentage =
    totalEvents > 0
      ? Math.round(
        (processed /
          totalEvents) *
        100
      )
      : 0;

  return (
    <Grid
      container
      direction="row"
      alignItems="flex-start"
    >
      <Grid item xs={12}>
        <Typography
          gutterBottom
          variant="h2"
          component="h2"
        >
          Depósitos históricos
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <Paper
          className={
            classes.paper
          }
        >
          <Typography variant="body1">
            Selecciona el rango de fechas
            de los eventos que deseas
            incluir en el reporte.
          </Typography>

          <Grid
            container
            direction="row"
            alignItems="center"
          >
            <Grid item>
              <TextField
                label="Fecha de inicio"
                type="date"
                value={
                  startDate
                }
                onChange={(e) =>
                  setStartDate(
                    e.target.value
                  )
                }
                className={
                  classes.dateField
                }
                InputLabelProps={{
                  shrink: true,
                }}
                disabled={
                  loading
                }
              />
            </Grid>

            <Grid item>
              <TextField
                label="Fecha final"
                type="date"
                value={endDate}
                onChange={(e) =>
                  setEndDate(
                    e.target.value
                  )
                }
                className={
                  classes.dateField
                }
                InputLabelProps={{
                  shrink: true,
                }}
                disabled={
                  loading
                }
              />
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              className={
                classes.button
              }
              onClick={
                downloadDeposits
              }
              disabled={
                loading ||
                !eventsList ||
                !startDate ||
                !endDate
              }
            >
              {loading
                ? "Procesando eventos..."
                : "Descargar reporte CSV"}
            </Button>
          </Grid>

          {loading && (
            <>
              <LinearProgress
                className={
                  classes.progress
                }
                variant="determinate"
                value={
                  percentage
                }
              />

              <Typography
                variant="body2"
                className={
                  classes.status
                }
              >
                Eventos procesados:{" "}
                {processed} de{" "}
                {totalEvents} (
                {percentage}%)
              </Typography>

              <Typography variant="body2">
                Eventos con depósitos:{" "}
                {
                  eventsWithDeposits
                }
              </Typography>

              <Typography variant="body2">
                Movimientos encontrados:{" "}
                {
                  totalMovements
                }
              </Typography>
            </>
          )}

          {!loading &&
            totalEvents > 0 && (
              <>
                <Typography
                  variant="body2"
                  className={
                    classes.status
                  }
                >
                  Eventos revisados:{" "}
                  {totalEvents}
                </Typography>

                <Typography variant="body2">
                  Eventos con depósitos:{" "}
                  {
                    eventsWithDeposits
                  }
                </Typography>

                <Typography variant="body2">
                  Movimientos encontrados:{" "}
                  {
                    totalMovements
                  }
                </Typography>
              </>
            )}
        </Paper>
      </Grid>
    </Grid>
  );
}